import {
  trackDynatraceChargerStatusUpdated,
  trackDynatraceSessionMetrics,
  type DynatraceLiveStatusSource,
  type DynatraceScheduleStatus,
} from '../../utils/native/dynatrace-events';

type LastStatusUpdate = { chargerId: string; at: number } | null;
type LiveChargerContext = DynatraceScheduleStatus & { status: string };
type SessionMetrics = {
  sessionStartedAt?: string | null;
  sessionEndedAt?: string | null;
  powerKw?: number | null;
  energyKwh?: number | null;
  costAmount?: number | null;
  currency?: string | null;
};
type LastSessionMetrics = { chargerId: string; signature: string } | null;

export function createLiveStatusTelemetry() {
  let lastStatusUpdate: LastStatusUpdate = null;
  let lastSessionMetrics: LastSessionMetrics = null;
  const liveChargerContexts = new Map<string, LiveChargerContext>();

  function getStatusFreshnessMs(chargerId: string | null) {
    if (!chargerId || lastStatusUpdate?.chargerId !== chargerId) return undefined;
    return Math.max(0, Date.now() - lastStatusUpdate.at);
  }

  function recordStatusUpdate(
    chargerId: string | null,
    source: DynatraceLiveStatusSource,
    status: string,
    sourceTimestamp: string | null | undefined,
    scheduleStatus?: DynatraceScheduleStatus,
    powerKw?: number | null
  ) {
    if (!chargerId) return;
    const now = Date.now();
    const previous = lastStatusUpdate;
    lastStatusUpdate = { chargerId, at: now };
    const liveContext = { ...liveChargerContexts.get(chargerId), ...scheduleStatus, status };
    liveChargerContexts.set(chargerId, liveContext);
    const parsedTimestamp = sourceTimestamp ? Date.parse(sourceTimestamp) : Number.NaN;
    trackDynatraceChargerStatusUpdated({
      chargerId,
      status,
      source,
      scheduleStatus: liveContext,
      powerKw,
      statusFreshnessMs: 0,
      ...(previous?.chargerId === chargerId ? { statusUpdateIntervalMs: Math.max(0, now - previous.at) } : {}),
      ...(Number.isFinite(parsedTimestamp) ? { upstreamLatencyMs: Math.max(0, now - parsedTimestamp) } : {}),
    });
  }

  function recordSessionMetrics(chargerId: string | null, source: DynatraceLiveStatusSource, metrics: SessionMetrics) {
    if (!chargerId) return;
    const context = liveChargerContexts.get(chargerId);
    const signature = JSON.stringify({ source, context, ...metrics });
    if (lastSessionMetrics?.chargerId === chargerId && lastSessionMetrics.signature === signature) return;
    lastSessionMetrics = { chargerId, signature };
    trackDynatraceSessionMetrics({
      chargerId,
      source,
      ...(context ? { status: context.status, scheduleStatus: context } : {}),
      ...metrics,
    });
  }

  return { getStatusFreshnessMs, recordStatusUpdate, recordSessionMetrics };
}
